import { JSX } from "preact";

import { affiliateNetworks } from "../../settings/affiliate";

export { affiliateMerchants } from "../../settings/affiliate";

export { default as ProductLink } from "./product-link";
export { default as Button } from "./button";
export { default as ButtonList } from "./button-list";
export { default as ButtonGroup } from "./button-group";
export { default as ButtonDualGroup } from "./button-dual-group";
export { default as ButtonBar } from "./button-bar";
export { default as StickyButtons } from "./sticky-buttons";
export { default as OptionsButton } from "./options-button";
export { default as LargeViewOnly } from "./large-view-only";
export { default as SmallViewOnly } from "./small-view-only";

const affiliateEnabled = process.env.AFFILIATE_enabled === "true";

export interface AffiliateData {
  name: string;
  id: number;
  network: string;
  enabled: boolean;
}

export type ButtonSize = "normal" | "medium" | "small";

export interface LinkProps extends Omit<
  JSX.AnchorHTMLAttributes<HTMLAnchorElement>,
  "href"
> {
  buttonSize?: ButtonSize;

  href: string;
  affiliate?: string;
  affiliateData?: AffiliateData;
  external?: boolean;
  nofollow?: boolean;
  blank?: boolean;
}

// Utility function to calculate processed anchor tag attributes. Can be reused by <Button> etc.
export const getLinkProps = <P extends LinkProps>(props: P) => {
  const {
    href,
    affiliate,
    affiliateData,
    external = false,
    nofollow = false,
    blank,
    target,
    rel,
    class: className,
    buttonSize,
    ...restProps
  } = props;

  let finalHref = href;
  let finalTarget = blank == true ? "_blank" : target;
  let relValues = rel ? rel.split(/\s+/).filter(Boolean) : [];

  let sponsored = false;

  // Affiliate merchant link
  if (affiliateEnabled && affiliateData) {
    const network = affiliateNetworks[affiliateData.network];
    if (network) {
      const merchantId = network.merchants[affiliateData.name];
      if (merchantId && network.id && typeof network.buildLink === "function") {
        finalHref = network.buildLink(merchantId, network.id, href);
        sponsored = true;
      } else {
        console.warn(
          "AffiliateLink: Invalid merchant or missing buildLink function"
        );
      }
    } else {
      console.warn("AffiliateLink: Invalid network name");
    }
  }

  // Affiliate basic link
  if (affiliateEnabled && !sponsored && affiliate) {
    finalHref = affiliate;
    sponsored = true;
  }

  // External links and any with affiliate props
  if (external === true || affiliateData || affiliate) {
    if (blank != false) {
      finalTarget = "_blank";
    }
    relValues.push("noopener", "noreferrer");
  }

  // Sponsored links
  if (sponsored) {
    relValues.push("sponsored");
  }

  if (nofollow) {
    relValues.push("nofollow");
  }

  // Deduplicate and join
  const mergedRel = Array.from(new Set(relValues)).join(" ");

  return {
    ...restProps,
    href: finalHref,
    target: finalTarget || undefined,
    rel: mergedRel || undefined,
    class: className,
  };
};

const Link = (props: LinkProps) => {
  const linkProps = getLinkProps(props);
  return <a {...linkProps} />;
};

export default Link;
